import React, { FunctionComponent, useEffect, useState } from "react";

import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from "@material-ui/core";
import { SegmentRule, Segment } from "../../../models/Segment";
import { listRules } from "../../../api/segmentApi";
import { isOk } from "../../../result";
import useStyles from "./style";
import { Rule } from "./Rule";
import { AddCircle } from "@material-ui/icons";
import { SaveSegmentDialog } from "./SaveSegmentDialog";
import { useI18n } from "../../../hooks/useI18n";

export interface SegmentEditorProps {
  segment: Segment;
  onSave: () => void;
}

export const SegmentEditor: FunctionComponent<SegmentEditorProps> = ({
  segment,
  onSave
}) => {
  const classes = useStyles();
  const { t } = useI18n();

  const [state, setState] = useState({
    saveSegment: false,
    ruleTypes: [] as SegmentRule[],
    ruleInstances: segment.rules ?? ([] as SegmentRule[])
  });

  const [addRule, setAddRule] = useState(false);

  useEffect(() => {
    listRules().then(response => {
      if (isOk(response)) {
        setState({ ...state, ruleTypes: [...response.value] });
      }
    });
  }, []);

  const populateRuleTypes = (): SegmentRule[] => {
    return state.ruleTypes.sort((a: any, b: any) => a.hasQuery - b.hasQuery);
  };

  const addRuleInstance = (e: any) => {
    const rule = state.ruleTypes.find(r => r.id == e.target.value);

    if (rule === undefined) return;

    const deepCopyRule = JSON.parse(JSON.stringify(rule));

    const instances = [
      ...state.ruleInstances,
      { ...deepCopyRule, order: state.ruleInstances.length + 1 }
    ];

    handleAddRule(false);

    setState({
      ...state,
      ruleInstances: instances
    });
  };

  const handleAddRule = (add: boolean) => {
    setAddRule(add);
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant={"subtitle1"}>
            {t("common.matchTheFollowingConditions")}{" "}
            {state.ruleInstances.length
              ? ` ${t("common.formFields.forCustomersWho")}`
              : ""}
            :
          </Typography>
        </Grid>
        <Grid item xs={12}>
          {state.ruleInstances.map((x, i) => (
            <>
              <Rule
                rule={{ ...x, order: i }}
                key={i}
                onChange={rule => {
                  const rules = [...state.ruleInstances];
                  rules[i] = rule;
                  setState({ ...state, ruleInstances: rules });
                }}
                onDelete={id => {
                  setState({
                    ...state,
                    ruleInstances: state.ruleInstances.filter(x => x.id !== id)
                  });
                }}
              />
              {state.ruleInstances.length > 1 &&
                i < state.ruleInstances.length - 1 && (
                  <Typography
                    color={"primary"}
                    style={{
                      textAlign: "center",
                      fontSize: "0.8rem",
                      fontWeight: "bold"
                    }}
                  >
                    {t("common.and")}
                  </Typography>
                )}
            </>
          ))}
        </Grid>
        <Grid item xs={6}>
          {state.ruleInstances.length > 0 && (
            <Button
              onClick={() => handleAddRule(true)}
              style={{ display: !addRule ? "flex" : "none" }}
              color={"primary"}
              startIcon={<AddCircle style={{ color: "rgb(31, 132, 52)" }} />}
            >
              {t("common.addAnother")}
            </Button>
          )}

          <FormControl
            style={{
              display:
                state.ruleInstances.length === 0 || addRule ? "flex" : "none"
            }}
            className={classes.field}
            variant={"standard"}
            required
          >
            <InputLabel id="type-select-label">
              {t("common.formFields.forCustomersWho")}
            </InputLabel>
            <Select
              labelId="type-select-label"
              value={""}
              open={addRule}
              onClose={() => handleAddRule(false)}
              onOpen={() => handleAddRule(true)}
              onChange={addRuleInstance}
            >
              {populateRuleTypes().map(r => (
                <MenuItem
                  key={r.id}
                  value={r.id}
                  disabled={state.ruleInstances.some(ur => ur.id === r.id)}
                >
                  {r.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button
            style={{ width: 200, marginTop: 20 }}
            variant="contained"
            disabled={state.ruleInstances.length === 0}
            color="primary"
            onClick={() => setState({ ...state, saveSegment: true })}
          >
            {segment.id
              ? t("common.actions.editSegment")
              : t("common.actions.createSegment")}
          </Button>
        </Grid>
      </Grid>
      <SaveSegmentDialog
        open={state.saveSegment}
        segmentId={segment.id}
        rules={state.ruleInstances}
        segmentName={segment.name}
        onSave={() => {
          setState({ ...state, saveSegment: false });
          onSave();
        }}
        onCancel={() => setState({ ...state, saveSegment: false })}
      />
    </>
  );
};
